/**
 * 按当前表的字段，从内置单据里挑出最像的几张推荐给用户。
 *
 * 为什么要有它：线上数据显示 326 个打开过插件的用户里 272 个（83%）从没打印过
 * 一次。他们面对的是一张白纸加一个「新建模板」按钮 —— 得自己想清楚要排什么、
 * 加哪些区块、每个区块绑哪个字段。竞品在这一步直接给三张已经绑好字段的单据，
 * 点一下就能预览真数据。这个模块就是补这一段。
 *
 * 匹配用的是渲染层同一套 findFieldByRef（精确 id → 精确名 → 归一 → 包含），
 * 保证「这里说匹配上了」和「打印时真的取到值」是一回事，不会推荐完一片空白。
 */
import { findFieldByRef } from '../../render-core/fieldMatch';
import type { FieldMeta } from '../api/types';
import { LIBRARY, type LibraryTemplate } from './library';

export interface Recommendation {
  lib: LibraryTemplate;
  /** 命中的表字段名（去重，按模板里声明的顺序）。 */
  matched: string[];
  /** 命中数 / 模板声明的字段数，0~1。 */
  ratio: number;
  /** 真正对上了（命中率过线）；false = 凑数的，字段要靠名称自动匹配或用户自己绑。 */
  strict: boolean;
}

/** 低于这个命中率就不推荐了 —— 硬凑出来的推荐比不推荐更伤信任。 */
const MIN_RATIO = 0.4;
/** 至少要对上这么多字段，否则一两个泛词（「备注」）也能凑出比例。 */
const MIN_MATCHED = 2;

/**
 * @param fieldMetas 当前表的字段
 * @param limit      最多返回几张
 */
export function recommendTemplates(
  fieldMetas: Pick<FieldMeta, 'id' | 'name'>[],
  limit = 3,
): Recommendation[] {
  if (!fieldMetas || fieldMetas.length === 0) return [];
  const out: Recommendation[] = [];
  const rest: Recommendation[] = [];
  for (const lib of LIBRARY) {
    const seen = new Set<string>();
    const matched: string[] = [];
    for (const want of lib.fields) {
      const hit = findFieldByRef(fieldMetas, want);
      if (hit && !seen.has(hit.id)) {
        seen.add(hit.id);
        matched.push(hit.name);
      }
    }
    const ratio = lib.fields.length > 0 ? matched.length / lib.fields.length : 0;
    if (matched.length >= MIN_MATCHED && ratio >= MIN_RATIO) {
      out.push({ lib, matched, ratio, strict: true });
    } else {
      rest.push({ lib, matched, ratio, strict: false });
    }
  }
  // 命中率高的排前面；相同则命中字段多的优先
  const byScore = (a: Recommendation, b: Recommendation) => b.ratio - a.ratio || b.matched.length - a.matched.length;
  out.sort(byScore);
  rest.sort(byScore);
  // 推荐区永远有三张：对不上就拿最接近的凑 —— 一张白纸比一张不太准的单据更劝退，
  // 用户拿到手上改两个字段总比自己从零搭强。凑数的会标出来。
  return [...out, ...rest].slice(0, limit);
}
